/**
 * ManageCustomersPage
 *
 * Super admin page for viewing and managing all Customer nodes.
 * Supports:
 *   - List all customers (User and Org) with search
 *   - Create new Org Customer (syncs to QBO, no payment setup required)
 *   - Inline expand to edit account details
 *   - Quick access to billing settings for any customer
 */

import React, { useState, useContext, useCallback, Fragment } from 'react';
import {
    Button, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Chip, Tooltip,
    CircularProgress, Alert, Skeleton, InputAdornment, Divider,
    Collapse, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

import { PageHeader } from '../design-system';
import styles from './page-admin-customer.module.css';

import UserProfileContext from '../context/userprofilecontext.js';
import SnackbarContext from '../context/snackbar-context.js';
import { useCustomersForCurrentUser } from '../api/swrcurrentusercontext.js';
import { mutate } from 'swr';
import { useNavigate } from 'react-router-dom';
import { useGetAvailableStrategies } from '../api/api-billing-settings.js';
import { updateCustomerDetails } from '../api/api-billing-settings.js';
import {adminCreateOrgCustomer} from '../api/api-billing-settings.js';



// ──────────────────────────────────────────────────────────────────────
// Create Customer Dialog
// ──────────────────────────────────────────────────────────────────────

function CreateCustomerDialog({ open, onClose, onCreate, isCreating }) {
    const [formData, setFormData] = useState({
        orgName: '',
        orgEmail: '',
        orgPhone: '',
        billingStrategy: 'paygo',
    });
    const [error, setError] = useState(null);

    const { data: strategies, isLoading: isLoadingStrategies } = useGetAvailableStrategies(open);

    const handleCreate = () => {
        if (!formData.orgName.trim()) {
            setError('Organization name is required');
            return;
        }
        if (!formData.orgEmail.trim()) {
            setError('Billing email is required');
            return;
        }
        setError(null);
        onCreate({
            orgName: formData.orgName.trim(),
            orgEmail: formData.orgEmail.trim(),
            orgPhone: formData.orgPhone.trim() || null,
            billingStrategy: formData.billingStrategy,
        });
    };

    const handleClose = () => {
        setFormData({ orgName: '', orgEmail: '', orgPhone: '', billingStrategy: 'paygo' });
        setError(null);
        onClose();
    };

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            setFormData({ orgName: '', orgEmail: '', orgPhone: '', billingStrategy: 'paygo' });
            setError(null);
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <BusinessIcon style={{ color: 'var(--color-navy-700)' }} />
                    <span style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--text-primary)',
                    }}>
                        Create Organization Customer
                    </span>
                </span>
            </DialogTitle>
            <DialogContent>
                <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                    marginBottom: 'var(--space-5)',
                    marginTop: 0,
                }}>
                    Creates the organization in the system and syncs to QuickBooks Online.
                    Payment methods can be added later from the billing settings.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <TextField
                        autoFocus
                        label="Organization Name"
                        value={formData.orgName}
                        onChange={(e) => setFormData(prev => ({ ...prev, orgName: e.target.value }))}
                        fullWidth
                        required
                        disabled={isCreating}
                    />
                    <TextField
                        label="Billing Email"
                        value={formData.orgEmail}
                        onChange={(e) => setFormData(prev => ({ ...prev, orgEmail: e.target.value }))}
                        fullWidth
                        required
                        disabled={isCreating}
                        helperText="Invoices and billing notifications will be sent here"
                    />
                    <TextField
                        label="Phone"
                        value={formData.orgPhone}
                        onChange={(e) => setFormData(prev => ({ ...prev, orgPhone: e.target.value }))}
                        fullWidth
                        disabled={isCreating}
                    />
                    <FormControl fullWidth size="small" disabled={isCreating || isLoadingStrategies}>
                        <InputLabel>Billing Strategy</InputLabel>
                        <Select
                            value={formData.billingStrategy}
                            label="Billing Strategy"
                            onChange={(e) => setFormData(prev => ({ ...prev, billingStrategy: e.target.value }))}
                        >
                            {strategies && Object.entries(strategies).map(([key, data]) => (
                                <MenuItem key={key} value={key}>
                                    {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                    {data.monthlyAmount != null ? ` ($${data.monthlyAmount}/mo)` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleClose} disabled={isCreating}>Cancel</Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={isCreating || !formData.orgName.trim() || !formData.orgEmail.trim()}
                    startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                >
                    {isCreating ? 'Creating...' : 'Create Customer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Customer Row (expandable)
// ──────────────────────────────────────────────────────────────────────

function CustomerRow({ customer, onNavigateBilling, onRefresh }) {
    const snackbarContext = useContext(SnackbarContext);
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const isOrg = customer.customerType === 'organization';
    const hasPayment = customer.paymentMethodStatus === 'active';
    const displayName = isOrg
        ? customer.orgName
        : `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.customerID;

    const [formData, setFormData] = useState({
        orgName: customer.orgName || '',
        orgEmail: customer.orgEmail || '',
        orgPhone: customer.orgPhone || '',
        phone: customer.phone || '',
    });

    React.useEffect(() => {
        setFormData({
            orgName: customer.orgName || '',
            orgEmail: customer.orgEmail || '',
            orgPhone: customer.orgPhone || '',
            phone: customer.phone || '',
        });
    }, [customer]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            const payload = { customerID: customer.customerID };
            if (isOrg) {
                payload.orgName = formData.orgName.trim();
                payload.orgEmail = formData.orgEmail.trim();
                payload.orgPhone = formData.orgPhone.trim();
            } else {
                payload.phone = formData.phone.trim();
            }
            await updateCustomerDetails(payload);
            snackbarContext.openSnackbarSuccess('Customer updated');
            setEditing(false);
            onRefresh();
        } catch (error) {
            snackbarContext.openSnackbarError('Failed to update customer');
        } finally {
            setIsSaving(false);
        }
    }, [customer.customerID, formData, isOrg, snackbarContext, onRefresh]);

    return (
        <div className={styles.customerRow}>
            {/* Summary row */}
            <div
                className={styles.rowSummary}
                onClick={() => setExpanded(!expanded)}
            >
                {isOrg ? (
                    <BusinessIcon style={{ color: 'var(--color-navy-700)', fontSize: 24 }} />
                ) : (
                    <PersonIcon style={{ color: 'var(--text-secondary)', fontSize: 24 }} />
                )}

                <div className={styles.customerInfo}>
                    <div className={styles.customerName}>
                        {displayName}
                    </div>
                    <div className={styles.customerEmail}>
                        {isOrg ? customer.orgEmail || '' : customer.email || ''}
                    </div>
                </div>

                <Chip
                    label={isOrg ? 'Organization' : 'Individual'}
                    size="small"
                    variant="outlined"
                    color={isOrg ? 'primary' : 'default'}
                    sx={{ fontSize: 'var(--text-xs)' }}
                />

                <Chip
                    label={customer.billingStrategy
                        ? customer.billingStrategy.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                        : 'No Strategy'
                    }
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 'var(--text-xs)' }}
                />

                {hasPayment ? (
                    <Tooltip title="Payment method on file">
                        <CheckCircleIcon color="success" fontSize="small" />
                    </Tooltip>
                ) : (
                    <Tooltip title="No payment method">
                        <WarningIcon color="warning" fontSize="small" />
                    </Tooltip>
                )}

                <Tooltip title="Billing Settings">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onNavigateBilling(customer);
                        }}
                    >
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>

            {/* Expanded details */}
            <Collapse in={expanded}>
                <Divider />
                <div className={styles.detailsPane}>
                    {isOrg ? (
                        <div className={styles.fieldGroup}>
                            {editing ? (
                                <div className={styles.fieldGroupEditing}>
                                    <TextField
                                        label="Organization Name"
                                        value={formData.orgName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, orgName: e.target.value }))}
                                        fullWidth
                                        size="small"
                                        disabled={isSaving}
                                    />
                                    <TextField
                                        label="Billing Email"
                                        value={formData.orgEmail}
                                        onChange={(e) => setFormData(prev => ({ ...prev, orgEmail: e.target.value }))}
                                        fullWidth
                                        size="small"
                                        disabled={isSaving}
                                    />
                                    <TextField
                                        label="Phone"
                                        value={formData.orgPhone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, orgPhone: e.target.value }))}
                                        fullWidth
                                        size="small"
                                        disabled={isSaving}
                                    />
                                    <div className={styles.editActions}>
                                        <Button variant="contained" size="small" onClick={handleSave} disabled={isSaving}>
                                            {isSaving ? <CircularProgress size={16} /> : 'Save'}
                                        </Button>
                                        <Button size="small" onClick={() => setEditing(false)} disabled={isSaving}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.fieldGroupReadonly}>
                                    <div className={styles.fieldGroup}>
                                        <div>
                                            <span className={styles.fieldLabel}>Organization Name</span>
                                            <span className={styles.fieldValue}>{customer.orgName}</span>
                                        </div>
                                        <div>
                                            <span className={styles.fieldLabel}>Billing Email</span>
                                            <span className={styles.fieldValue}>{customer.orgEmail || '\u2014'}</span>
                                        </div>
                                        <div>
                                            <span className={styles.fieldLabel}>Phone</span>
                                            <span className={styles.fieldValue}>{customer.orgPhone || '\u2014'}</span>
                                        </div>
                                        <div>
                                            <span className={styles.fieldLabel}>QBO Customer ID</span>
                                            <span className={styles.fieldValueMono}>
                                                {customer.qboCustomerId || '\u2014'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className={styles.fieldLabel}>Customer ID</span>
                                            <span className={styles.fieldValueMonoSmall}>
                                                {customer.customerID}
                                            </span>
                                        </div>
                                    </div>
                                    <IconButton size="small" onClick={() => setEditing(true)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.fieldGroup}>
                            <div>
                                <span className={styles.fieldLabel}>Name</span>
                                <span className={styles.fieldValue}>
                                    {customer.firstName} {customer.lastName}
                                </span>
                            </div>
                            <div>
                                <span className={styles.fieldLabel}>Email</span>
                                <span className={styles.fieldValue}>{customer.email || '\u2014'}</span>
                            </div>
                            <div>
                                <span className={styles.fieldLabel}>Phone</span>
                                <span className={styles.fieldValue}>{customer.phone || '\u2014'}</span>
                            </div>
                            <div>
                                <span className={styles.fieldLabel}>QBO Customer ID</span>
                                <span className={styles.fieldValueMono}>
                                    {customer.qboCustomerId || '\u2014'}
                                </span>
                            </div>
                            <div>
                                <span className={styles.fieldLabel}>Customer ID</span>
                                <span className={styles.fieldValueMonoSmall}>
                                    {customer.customerID}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Collapse>
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────────────────────────────

export default function ManageCustomersPage() {
    const snackbarContext = useContext(SnackbarContext);
    const userProfileContext = useContext(UserProfileContext);
    const navigate = useNavigate();

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    const isSuperAdmin = userProfileContext?.userProfile?.isSuperAdmin;
    const { data: customers, isLoading, error: fetchError, mutate: refreshCustomers } = useCustomersForCurrentUser();

    const handleRefresh = useCallback(() => {
        refreshCustomers();
        mutate('CUSTOMERS_FOR_CURRENT_USER');
    }, [refreshCustomers]);

    const handleCreate = useCallback(async (payload) => {
        setIsCreating(true);
        try {
            await adminCreateOrgCustomer(payload);
            snackbarContext.openSnackbarSuccess(`Organization "${payload.orgName}" created`);
            setCreateDialogOpen(false);
            handleRefresh();
        } catch (error) {
            console.error('Error creating customer:', error);
            let msg = 'Failed to create customer';
            try {
                const resp = error._response || error.response;
                if (resp?.body) {
                    let body = resp.body;
                    if (typeof body === 'string') {
                        body = body.replace(/'/g, '"');
                        const parsed = JSON.parse(body);
                        msg = parsed.message || parsed.error || msg;
                    }
                }
            } catch (parseErr) { /* ignore */ }
            snackbarContext.openSnackbarError(msg);
        } finally {
            setIsCreating(false);
        }
    }, [snackbarContext, handleRefresh]);

    const handleNavigateBilling = useCallback((customer) => {
        // Set this customer as active, then navigate to billing settings
        if (userProfileContext?.setCurrentCustomer) {
            userProfileContext.setCurrentCustomer(customer);
        }
        navigate('/billing_settings');
    }, [navigate, userProfileContext]);

    // Guard: super admin only
    if (!isSuperAdmin) {
        return (
            <div className={styles.guardContainer}>
                <Alert severity="warning">
                    This page is only accessible to super administrators.
                </Alert>
            </div>
        );
    }

    // Filter customers
    const customersArray = customers || [];
    const filteredCustomers = customersArray.filter((c) => {
        // Type filter
        if (typeFilter === 'org' && c.customerType !== 'organization') return false;
        if (typeFilter === 'user' && c.customerType !== 'user') return false;

        // Search filter
        if (!searchFilter) return true;
        const term = searchFilter.toLowerCase();
        const name = c.customerType === 'organization'
            ? (c.orgName || '').toLowerCase()
            : `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
        const email = (c.orgEmail || c.email || '').toLowerCase();
        return name.includes(term) || email.includes(term) || (c.customerID || '').toLowerCase().includes(term);
    });

    return (
        <Fragment>
            <div className={styles.page}>
                {/* Header */}
                <PageHeader
                    title="Customer Management"
                    subtitle={`${customersArray.length} customer${customersArray.length !== 1 ? 's' : ''}`}
                    actions={
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            New Organization
                        </Button>
                    }
                />

                {/* Filters */}
                <div className={styles.filtersRow}>
                    <TextField
                        placeholder="Search customers..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'var(--text-disabled)' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={typeFilter}
                            label="Type"
                            onChange={(e) => setTypeFilter(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="org">Organizations</MenuItem>
                            <MenuItem value="user">Individuals</MenuItem>
                        </Select>
                    </FormControl>
                </div>

                {/* Error */}
                {fetchError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to load customers. Please refresh the page.
                    </Alert>
                )}

                {/* Loading */}
                {isLoading ? (
                    <div className={styles.skeletonList}>
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: 'var(--radius-lg)' }} />
                        ))}
                    </div>
                ) : filteredCustomers.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p className={styles.emptyStateText}>
                            {searchFilter ? `No customers match "${searchFilter}"` : 'No customers found'}
                        </p>
                    </div>
                ) : (
                    <div className={styles.customerList}>
                        {filteredCustomers.map((customer) => (
                            <CustomerRow
                                key={customer.customerID}
                                customer={customer}
                                onNavigateBilling={handleNavigateBilling}
                                onRefresh={handleRefresh}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create Dialog */}
            <CreateCustomerDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onCreate={handleCreate}
                isCreating={isCreating}
            />
        </Fragment>
    );
}
